import { Modal, List, ListItem, ListItemButton, SvgIcon, Tooltip } from "@mui/material";
import { useContext, useEffect, useId, useState } from "react";
import { ThemeWrapper, useMobileLayout } from "@verity/components";
import { CoreIconButton, CorePaper, getScrollbarThemeOverrides } from "@verity/components/core";
import { PortalControllerContext } from "../context";
import { portalNavigation, PortalNavigationEntry, PortalNavigationSection } from "../portal-config";
import CaretDown from "../../images/portal-navigation/caretDown.svg?react";
import HamburgerIcon from "../../images/portal-navigation/hamburgerIcon.svg?react";
import styles from "./portal-menu.module.css";
import i18n from "../../i18n";

function MenuEntry({ entry }: { entry: PortalNavigationEntry }) {
	const { icon, text, pageKey } = entry;
	const { currPage, navigateToPage } = useContext(PortalControllerContext);
	const isMobileLayout = useMobileLayout();
	return (
		// Added ListItem for <li> tag
		<ListItem disableGutters disablePadding>
			<ListItemButton
				className={`${styles.entry} ${currPage === pageKey ? styles.active : ""}`}
				onClick={() => navigateToPage(pageKey)}
				aria-label={text}
				data-menuitem
			>
				<SvgIcon className={styles.entryIcon}>{icon}</SvgIcon>
				<div className={`${styles.entryLabel} ${isMobileLayout ? styles.entryLabelCompact : ""}`}>{text}</div>
			</ListItemButton>
		</ListItem>
	);
}

function MenuSection({
	section,
	expanded,
	setExpanded,
}: {
	section: PortalNavigationSection;
	expanded: boolean;
	setExpanded: (value: boolean) => void;
}) {
	const { icon, text } = section;
	const { pages, currPage, navigateToPage } = useContext(PortalControllerContext);
	const isMobileLayout = useMobileLayout();
	const availableEntries = section.entries
		? Object.values(section.entries).filter(({ pageKey }) => pages[pageKey])
		: [];
	const hasMenu = availableEntries.length > 0;

	return (section.pageKey && pages[section.pageKey]) || availableEntries.length > 0 ? (
		<ListItem className={`${styles.section} ${expanded ? styles.expanded : ""}`}>
			<ListItemButton
				className={`${styles.sectionButton} ${section.pageKey && currPage === section.pageKey ? styles.active : ""}`}
				aria-label={text}
				aria-haspopup={hasMenu}
				aria-expanded={hasMenu ? expanded : undefined}
				data-menuitem
				onClick={section.pageKey ? () => navigateToPage(section.pageKey) : () => setExpanded(!expanded)}
			>
				<SvgIcon className={styles.sectionIcon}>{icon}</SvgIcon>
				<div className={`${styles.sectionLabel} ${isMobileLayout ? styles.sectionLabelCompact : ""}`}>{text}</div>
				{hasMenu && (
					<SvgIcon className={`${styles.expandIcon} ${isMobileLayout ? styles.expandIconCompact : ""}`}>
						<CaretDown />
					</SvgIcon>
				)}
			</ListItemButton>
			{expanded && (
				<List className={`${styles.sectionEntries} ${isMobileLayout ? styles.sectionEntriesCompact : ""}`}>
					{availableEntries.map((entry) => (
						<MenuEntry key={entry.key} entry={entry} />
					))}
				</List>
			)}
		</ListItem>
	) : null;
}

function MenuContent({ expandedTopBar = false }: { expandedTopBar?: boolean }) {
	const { pages, currPage } = useContext(PortalControllerContext);
	const isMobileLayout = useMobileLayout();
	const [expandedSections, setExpandedSections] = useState<string[]>([]);

	useEffect(() => {
		setExpandedSections((sections) => sections.filter((key) => pages[key]));
	}, [pages]);

	useEffect(() => {
		const currPageSection = Object.values(portalNavigation).find(
			({ entries }) => entries && Object.values(entries).some(({ pageKey }) => pageKey === currPage),
		)?.key;
		if (currPage && pages[currPage] && currPageSection) {
			setExpandedSections([currPageSection]);
		}
	}, [currPage]);

	const handleKeyDown = (e: React.KeyboardEvent<HTMLUListElement>) => {
		const { key, currentTarget, target } = e;
		if (key !== "ArrowDown" && key !== "ArrowUp") return;

		const allMenuItems = Array.from(currentTarget.querySelectorAll<HTMLElement>("[data-menuitem]"));
		const curIndex = allMenuItems.indexOf((target as HTMLElement).closest("[data-menuitem]") as HTMLElement);
		if (curIndex === -1) return;

		e.preventDefault();
		const dir = key === "ArrowDown" ? 1 : -1;
		const nextIdx = (curIndex + dir + allMenuItems.length) % allMenuItems.length;
		allMenuItems[nextIdx].focus();
	};

	return (
		<CorePaper
			rounded={false}
			className={`${styles.menu} ${isMobileLayout ? styles.menuCompact : ""} ${expandedTopBar ? styles.expandedTopBar : ""}`}
			sx={{ ...getScrollbarThemeOverrides() }}
		>
			<List className={styles.menuList} onKeyDown={handleKeyDown}>
				{Object.values(portalNavigation).map((section) => (
					<MenuSection
						key={section.key}
						section={section}
						expanded={expandedSections.includes(section.key)}
						setExpanded={(expanded) => {
							const newSections = expandedSections.filter((sectionKey) => sectionKey !== section.key);
							setExpandedSections(expanded ? newSections.concat([section.key]) : newSections);
						}}
					/>
				))}
			</List>
		</CorePaper>
	);
}

export function PortalMenuButton({ expandedTopBar }: { expandedTopBar: boolean }) {
	const [menuShowing, setMenuShowing] = useState(false);
	const buttonId = useId();
	const popoverId = useId();

	return (
		<ThemeWrapper scopedCssBaselineProps={{ style: { backgroundColor: "inherit" } }}>
			<Tooltip title={i18n.t("portal.components.portalMenuTSX.portalMenu")}>
				<CoreIconButton
					id={buttonId}
					variant="plain"
					className={`${styles.navigationMenuButton} ${menuShowing ? styles.active : ""}`}
					onClick={() => setMenuShowing((prev) => !prev)}
					aria-haspopup
					aria-expanded={menuShowing}
					aria-controls={popoverId}
					sx={{ backgroundColor: menuShowing ? "#dad0ea" : "inherit" }}
				>
					<HamburgerIcon />
				</CoreIconButton>
			</Tooltip>
			{menuShowing && (
				<Modal open onClose={() => setMenuShowing(false)} slotProps={{ backdrop: { invisible: true } }}>
					<MenuContent expandedTopBar={expandedTopBar} />
				</Modal>
			)}
		</ThemeWrapper>
	);
}

export default function PortalMenu() {
	return (
		<ThemeWrapper scopedCssBaselineProps={{ className: styles.menuHolder }}>
			<MenuContent />
		</ThemeWrapper>
	);
}
